/**
 * The windsock, seen from above, as geometry (pure, so it is tested rather than eyeballed).
 *
 * A standard airport windsock has five bands and is drawn straight out by about 3 kt per band: one band
 * stands out at 3 kt, the whole sock at 15 kt and above [U: the usual FAA rule of thumb; confirm against
 * AC 150/5345-27]. From above:
 * - the sock streams DOWNWIND from the pole, so it points away from where the wind comes from;
 * - each band that the wind holds out is drawn full length; the rest hang limp, which from above shows
 *   as a short, narrow stub;
 * - calm is a sock hanging straight down: from above, just its mouth around the pole;
 * - a gust is drawn as a faint outline of the sock at the gust's speed, behind the steady one.
 *
 * Angles are compass bearings: 0 = north (up), clockwise. Wind direction is where the wind comes FROM,
 * as reported.
 */

export const BANDS = 5
export const KT_PER_BAND = 3

/** Bands held straight out at this speed, 0–5, fractional. */
export function bandsOut(kt: number | null): number {
  if (kt === null || !Number.isFinite(kt) || kt <= 0) return 0
  return Math.min(BANDS, kt / KT_PER_BAND)
}

/** The bearing the sock points along: downwind, opposite the wind's direction. */
export function downwindBearing(fromDeg: number): number {
  return (fromDeg + 180) % 360
}

export interface Point {
  x: number
  y: number
}

/** A point `r` from the centre along a compass bearing. */
export function polar(cx: number, cy: number, bearingDeg: number, r: number): Point {
  const a = (bearingDeg * Math.PI) / 180
  return { x: cx + Math.sin(a) * r, y: cy - Math.cos(a) * r }
}

export interface Band {
  /** Polygon points, "x,y x,y …", ready for an SVG <polygon>. */
  points: string
  /** Alternating orange and white, starting orange at the mouth, like the real thing. */
  colour: 'orange' | 'white'
  /** Held out by the wind, or hanging limp. */
  out: boolean
}

export interface SockGeometry {
  bands: Band[]
  /** Total drawn length from the pole, px. */
  length: number
}

/**
 * The sock for a wind speed, pointing along `bearing`. Full length `fullLength` px; the mouth is
 * `mouthWidth` px wide and it tapers to `tipWidth`. Limp bands are a quarter of their length and
 * narrowed, so the sock visibly droops in light wind.
 */
export function sockGeometry(
  cx: number,
  cy: number,
  bearing: number,
  kt: number | null,
  fullLength = 100,
  mouthWidth = 26,
  tipWidth = 12,
): SockGeometry {
  const out = bandsOut(kt)
  const bandLen = fullLength / BANDS
  const bands: Band[] = []
  let dist = 0
  for (let i = 0; i < BANDS; i++) {
    const fraction = Math.max(0, Math.min(1, out - i)) // how much of this band the wind holds out
    const len = bandLen * (0.25 + 0.75 * fraction)
    const widthAt = (d: number, limpness: number) => {
      const taper = mouthWidth + ((tipWidth - mouthWidth) * d) / fullLength
      return taper * (1 - 0.45 * limpness)
    }
    const limp = 1 - fraction
    const w0 = widthAt(dist, limp) / 2
    const w1 = widthAt(dist + len, limp) / 2
    const a = polar(cx, cy, bearing, dist)
    const b = polar(cx, cy, bearing, dist + len)
    const left = (p: Point, w: number) => polar(p.x, p.y, bearing - 90, w)
    const right = (p: Point, w: number) => polar(p.x, p.y, bearing + 90, w)
    const pts = [left(a, w0), left(b, w1), right(b, w1), right(a, w0)]
    bands.push({
      points: pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
      colour: i % 2 === 0 ? 'orange' : 'white',
      out: fraction >= 0.5,
    })
    dist += len
  }
  return { bands, length: dist }
}
